import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Popover,
    TableSortLabel,
    Pagination } from '@mui/material';
import { ActionsIcon, ArrowLeftIcon, ArrowRightIcon, DownArrow, Edit, Trash } from '../../../../assets';
import { creativeUploadActions } from '../../../../redux/reducers/creativeUploadReducer';
import { downloadFiles, getSortedData } from '../../../../utility';
import { CustomButton, TableRowsSkeleton } from '../../../shared';
import ConfirmationDelete from '../confirmationDelete';
import PreviewCreative from '../previewCreative';
import { deleteCreative, getCreativeById, getCreatives } from '../../../../redux/thunks/creativeUploadThunk';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

const creativesTable = () => {
    const dispatch = useDispatch();
    const {
        creatives, isSelectedAll, selectedCreatives, isGetCreativeLoading, currentPage, pageSize, totalPages 
    } = useSelector(state => state.creativeUpload);
    const { company } = useSelector(state => state.company);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openPopoverId, setOpenPopoverId] = useState(null);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(null);
    const [openDelete, setOpenDelete] = useState(false);
    const [openPreview, setOpenPreview] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState(null);

    const Files_column = [
        { key: 'creative_name', value: 'Creative Name' },
        { key: 'creative_size', value: 'Creative Size' },
        { key: 'start_Date', value: 'Start Date' },
        { key: 'end_Date', value: 'End Date' },
        { key: 'options', value: '' }
    ];

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const createSortHandler = (property) => () => {
        handleRequestSort(property);
    };
    const sortedData = React.useMemo(() => getSortedData(orderBy, order, creatives), [order, orderBy, creatives]);
    const handlePopoverOpen = (event, id) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setOpenPopoverId(id);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setOpenPopoverId(null);
    };

    const handleSelectAll = () => {
        dispatch(creativeUploadActions.handleSelected({ type: 'selected_all' }));
    };

    const handleCheckboxChange = (id) => {
        dispatch(creativeUploadActions.handleSelected({ type: 'row_selected', selectedId: id }));
    };

    const handleTableRowClick = (event, id) => {
        const isCheckboxColumn = event.target.closest('.table-row-checkbox');
        const isActionsColumn = event.target.closest('.table-row-actions');
        const isPopoverOpen = Boolean(anchorEl);

        if (!isCheckboxColumn && !isActionsColumn && !isPopoverOpen) {
            handlePopoverClose();
        }
    };

    const onclose = () => {
        handlePopoverClose();
        setOpenDelete(!openDelete);
    };
    const handleDeleteOpen = () => {
        setOpenDelete(true);
    };

    const onclosePreview = () => {
        handlePopoverClose();
        setOpenPreview(!openPreview);
    };
    const handlePreviewOpen = (imageUrl) => {
        setPreviewImageUrl(imageUrl);
        setOpenPreview(true);
        handlePopoverClose();
    };

    const handleDelete = () => {
        handlePopoverClose();
        if (openPopoverId) {
            dispatch(deleteCreative({ companyId: company.id, data: { ids: [openPopoverId] } })).then(result => {
                if (result.payload.status_code === 200) {
                    dispatch(creativeUploadActions.setSelectedAllFalse());
                    dispatch(creativeUploadActions.handleSelected({ type: 'unselect' }));
                    dispatch(getCreatives({ companyId: company.id }));
                    onclose();
                }
            });
        }
    };

    const handleEditOpen = (id) => {
        handlePopoverClose();
        if(id){
            dispatch(creativeUploadActions.handleUploadModal({ type: 'edit' }));
            dispatch(creativeUploadActions.resetCreative());
            dispatch(getCreativeById({ Id: id }));
        }
        
    };

    const handlePageChange = (event, page) => {
        dispatch(creativeUploadActions.setCurrentPage(page));
    };

    const handleNextClick = () => {
        if (currentPage < totalPages) {
            handlePageChange(_, currentPage + 1);
        }
    };

    const handlePreviousClick = () => {
        if (currentPage > 1) {
            handlePageChange(_, currentPage - 1);
        }
    };
    const renderNoData = () => (
        <TableRow>
            <TableCell colSpan={5}>
                <Box p={3} textAlign="center">
                    <Typography className="text-sm-regular gray-900">
                        No creatives to download.
                    </Typography>
                </Box>
            </TableCell>
        </TableRow>
    );

    const renderTable = () => {
        const startIdx = (currentPage - 1) * pageSize;
        const endIdx = currentPage * pageSize;
        return sortedData?.length > 0 ? sortedData?.slice(startIdx, endIdx).map((row, index) => {
            const handleDownloadClick = () => {
                downloadFiles([{ file_url: row.file_path, file_name: row.creative_name }]);
                dispatch(creativeUploadActions.handleSelected({ type: 'unselect' }));
            };

            return (
                <TableRow className='cursor-pointer' key={row?.id} onClick={(event) => handleTableRowClick(event, row.id)}>
                    <TableCell className='table-row-data no-border table-row-checkbox'>
                        <Checkbox
                            checked={selectedCreatives.includes(row?.id)}
                            onChange={() => handleCheckboxChange(row.id)}
                        />
                    </TableCell>
                    <TableCell className='table-row-data no-border'>
                        {row?.creative_name}
                    </TableCell>
                    <TableCell className='table-row-data no-border font-weight-500 gray-900'>
                        {row?.creative_size}
                    </TableCell>
                    <TableCell className='table-row-data no-border font-weight-500 gray-900'>
                        {row?.start_date}
                    </TableCell>
                    <TableCell className='table-row-data no-border font-weight-500 gray-900'>
                        {row?.end_date}
                    </TableCell>
                    <TableCell className='table-row-data no-border cursor-pointer table-row-actions'>
                        <img height={20} src={ActionsIcon} width={20} onClick={(event) => handlePopoverOpen(event, row.id)} />
                        <Popover
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right'
                            }}
                            disableScrollLock={true}
                            open={openPopoverId === row.id}
                            sx={{
                                '& .MuiPaper-root': {
                                    boxShadow:
                                        '0px 4px 6px -2px rgba(16, 24, 40, 0.05), inset 0px 8px 10px -7px rgba(16, 24, 40, 0.10)'
                                }
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right'
                            }}
                            onClose={handlePopoverClose}
                        >
                            <Box>
                                <Box
                                    className="action-button-box table-row-actions"
                                    onClick={() => handlePreviewOpen(row?.file_path)}
                                >
                                    <VisibilityOutlinedIcon sx={{ fill: '#667085',width: '20px' }} />
                                    <Typography className="action-button-text">
                                        Preview
                                    </Typography>
                                </Box>
                                <Box
                                    className="action-button-box table-row-actions"
                                    onClick={handleDownloadClick}
                                >
                                    <DownArrow color='#667085' rectcolor="white" />
                                    <Typography className="action-button-text">
                                        Download
                                    </Typography>
                                </Box>
                                <Box
                                    className="action-button-box table-row-actions"
                                    onClick={()=>handleEditOpen(row?.id)}
                                >
                                    <Edit color='#667085' />
                                    <Typography className="action-button-text">
                                        Edit
                                    </Typography>
                                </Box>
                                <Box
                                    className="action-button-box table-row-actions"
                                    onClick={handleDeleteOpen}
                                >
                                    <Trash color='#667085' />
                                    <Typography className="action-button-text">
                                        Delete
                                    </Typography>
                                </Box>
                            </Box>
                        </Popover>
                    </TableCell>
                </TableRow>
            );
        }) : renderNoData();
    };

    return (
        <>
            {/* Sources History */}
            <Grid container spacing={'32px'}>
                {/* Sources History Table */}
                <Grid item xs={12}>
                    <Box className="b1px-border-box-br8px">
                        {/* Table */}
                        <Box>
                            <TableContainer className='no-box-shadow no-border' component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className="queries-headers-title bg-gray-50" data-testid="data_source_table_selectAll">
                                                <Checkbox
                                                    checked={isSelectedAll}
                                                    onChange={handleSelectAll}
                                                />
                                            </TableCell>
                                            {Files_column.map((column, index) => (
                                                <TableCell
                                                    align='left'
                                                    className='text-500fw-12fs-18lh bg-gray-50'
                                                    key={index}
                                                >
                                                    {column.key !== 'options' &&
                                                        <TableSortLabel
                                                            active={orderBy === column.key}
                                                            direction={orderBy === column.key ? order : 'asc'}
                                                            onClick={createSortHandler(column.key)}
                                                        >
                                                            {column.value}
                                                        </TableSortLabel>}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {isGetCreativeLoading ? (
                                            <TableRowsSkeleton
                                                columns={6}
                                                rows={10}
                                            />
                                        ) : (
                                            renderTable()
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                        {/* Table Footer */}
                        {totalPages > 1 ? (
                            <Box className="padding-tb-16-rl-24 border-top-gray">
                                <Box className="flex-row-space-between-center">
                                    <Box>
                                        <CustomButton
                                            buttonColor="#FFFFFF"
                                            buttonDisabled={!(currentPage !== 1)}
                                            customStyle={{ borderRadius: '8px' }}
                                            handleClick={handlePreviousClick}
                                            icon={<ArrowLeftIcon />}
                                            title="Previous"
                                            titleColor="#344054"
                                        />
                                    </Box>

                                    {/* Pagination */}
                                    <Box>
                                        <Pagination
                                            hideNextButton
                                            hidePrevButton
                                            count={totalPages}
                                            page={currentPage}
                                            shape="rounded"
                                            onChange={handlePageChange}
                                        />
                                    </Box>

                                    <Box>
                                        <CustomButton
                                            buttonColor="#FFFFFF"
                                            buttonDisabled={!(totalPages > currentPage)}
                                            customStyle={{ borderRadius: '8px' }}
                                            handleClick={handleNextClick}
                                            tailIcon={<ArrowRightIcon />}
                                            title="Next"
                                            titleColor="#344054"
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        ) : null}
                    </Box>
                </Grid>
            </Grid>
            <ConfirmationDelete
                open={openDelete}
                onClosed={onclose}
                onConfirms={handleDelete} />
            <PreviewCreative
                image_url={previewImageUrl}
                open={openPreview}
                onClosed={onclosePreview} 
                onConfirms={()=>{}}
            />
        </>
    );
};

export default creativesTable;
